import {
  createContext,
  useReducer,
  useContext,
  ReactNode,
  Dispatch,
} from "react";
import { listMetrics } from "../api/metrics";
import { handleRequestError } from "../helpers";

export type Metric = {
  ref: string;
  color: string | [string, string];
  short_name: string;
};
type MetricState = Metric[];
const MetricStateContext = createContext<MetricState | undefined>(undefined);
type MetricAction = { type: "setMetrics"; payload: MetricState };
type MetricDispatch = Dispatch<MetricAction>;
const MetricDispatchContext = createContext<MetricDispatch>(() => null);

function metricsReducer(state: MetricState, action: MetricAction) {
  switch (action.type) {
    case "setMetrics": {
      return action.payload;
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

function MetricsProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(metricsReducer, []);
  return (
    <MetricStateContext.Provider value={state}>
      <MetricDispatchContext.Provider value={dispatch}>
        {children}
      </MetricDispatchContext.Provider>
    </MetricStateContext.Provider>
  );
}

function useMetricsState() {
  const context = useContext(MetricStateContext);
  if (context === undefined) {
    throw new Error("useMetricsState must be used within a MetricsProvider");
  }
  return context;
}

async function fetchMetrics(dispatch: MetricDispatch, token: string) {
  try {
    const response = await listMetrics(token);
    const data = response.data
      ? response.data.map((item) => ({
          ...item,
          color: JSON.parse(item.color),
          limits: JSON.parse(item.limits),
          thresholds: JSON.parse(item.thresholds),
        }))
      : [];
    dispatch({ type: "setMetrics", payload: data });
    return data;
  } catch (e) {
    handleRequestError(e, "Failed fetching metrics: ");
  }
}

function useMetricsDispatch() {
  const context = useContext(MetricDispatchContext);
  if (context === undefined) {
    throw new Error("useMetricsDispatch must be used within a MetricsProvider");
  }
  return context;
}

export { MetricsProvider, useMetricsState, useMetricsDispatch, fetchMetrics };
